import User from '~/shared/src/model/User';
import {ColumnDefinition} from '~/shared/src/validationSchemes/modules/overview';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    ObjectDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    getEnumPropertyColumnDefinitions,
    getItemAttributeColumnDefinitions,
    getPropertyColumnDefinitions,
    getReferenceColumnDefinitions,
    getTargetArea
} from '~/shared/src/utilities/modules/finder';

/**
 * Builds the columnDefinitions
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {FinderBaseColumnDefinition[]}
 */
export const getColumnDefinitions = ( dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, user : User ) : ColumnDefinition[] => {
    let retVal : ColumnDefinition[] = [];

    // item attribute columnDefinitions
    retVal = retVal.concat( getItemAttributeColumnDefinitions( dataStructure ) );

    // reference columnDefinitions
    retVal = retVal.concat( getReferenceColumnDefinitions( dataStructure, schema, itemTypeName, user ) );

    // property columnDefinitions
    retVal = retVal.concat( getPropertyColumnDefinitions( dataStructure, schema, itemTypeName, user ) );

    // enum property columnDefinitions
    retVal = retVal.concat( getEnumPropertyColumnDefinitions( dataStructure, schema, itemTypeName, user ) );

    // metaData columnDefinitions
    retVal = retVal.concat( getItemMetaDataColumnDefinitions( dataStructure, status, timestamp ) );

    return retVal;
}

/**
 * Builds the columnDataDescriptors for the searchColumns
 *
 * @param {DataStructure} dataStructure
 *
 * @return {string[]}
 */
export const getSearchColumnDataDescriptors = ( dataStructure : DataStructure ) : DataDescriptor[ ] => {
    const retVal : DataDescriptor[]      = [],
          itemAttributeColumnDefinitions = getItemAttributeColumnDefinitions( dataStructure );

    for ( const itemAttributeColumnDefinition of itemAttributeColumnDefinitions ) {
        if ( itemAttributeColumnDefinition.dataDescriptor.attribute === 'key' || itemAttributeColumnDefinition.dataDescriptor.attribute === 'name' ) {
            retVal.push( itemAttributeColumnDefinition.dataDescriptor );
        }
    }

    return retVal;
}

/**
 * Builds the aliases for the searchColumns
 *
 * @param {DataStructure} dataStructure
 *
 * @return {string[]}
 */
export const getSearchColumnFieldNames = ( dataStructure : DataStructure ) : string[ ] => {
    const retVal : string[]           = [],
          searchColumnDataDescriptors = getSearchColumnDataDescriptors( dataStructure );

    for ( const searchColumnDataDescriptor of searchColumnDataDescriptors ) {
        retVal.push( searchColumnDataDescriptor.alias );
    }

    return retVal;
}

/**
 * Creates the column definitions for meta data
 *
 * @param {DataStructure} dataStructure
 * @param {Status} status
 * @param {String} timestamp
 *
 * @return {ColumnDefinition[]}
 */
export const getItemMetaDataColumnDefinitions = ( dataStructure : DataStructure, status : Status, timestamp : string ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [],
          targetArea                  = getTargetArea( status, timestamp );

    let columnDataDescriptor : DataDescriptor;

    // status
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.statusId' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.status',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : true,
        filter        : true,
        width         : 110
    } );

    // item meta data columns
    if ( targetArea === 'all' ) {

        // user
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.modifierUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );

        // valid from
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.validFromTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validFrom',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );

        // valid to
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.validToTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validTo',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );
    }

    else {

        // creation
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.creationTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.createdAt',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.creatorUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.createdBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        // modification
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.modificationTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedAt',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.modifierUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        // validation
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.validationTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validatedAt',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        columnDataDescriptor = buildDataDescriptor( { attributePath: 'item.validatorUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validatedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );
    }

    return retVal;
}
